/**
 * Upsell helpers (spec locked 2026-08-15): delta price labels, the
 * "prints beautifully up to" permission size, and the C-Type -> fine art
 * equivalent-size lookup. Pure functions, unit-tested in upsell.test.ts.
 *
 * PRICE-LABEL RULE (the "$0.00 never renders" spec): options are priced as
 * their DIFFERENCE from the current selection —
 *   selected row      -> no label at all (the picker's tick already says it)
 *   same price        -> the words "Same price", muted (never "$0.00"/"Free")
 *   costs more        -> "+$6.00"
 *   costs less        -> the muted ABSOLUTE price (owner 2026-08-16: hiding it
 *                        made a 20×30 customer hand-select every smaller size
 *                        just to price it; we still never advertise the SAVING,
 *                        so no "−$4.90" and no bold treatment)
 *   no current price  -> absolute price (nothing to be relative to yet)
 */

import { parseSizeId, type PaperId, type PricingData, type ResolvedSize } from '@rpl/pricing';
import { isCustomSizeId } from './customSize';
import { formatMoney } from './format';
import { checkPrint, type PrintCheckThresholds } from './printCheck';
import { paperForSize, priceForSafe, shortPaperLabel, sizeShort, sizeWithUnit } from './sizePaper';

export interface DeltaLabel {
  kind: 'absolute' | 'same' | 'up' | 'down';
  text: string;
  /** Muted rendering (the "Same price" words must not read as a price). */
  muted: boolean;
}

/** Delta label for a candidate price against the current selection's price. */
export function priceDeltaLabel(candidate: number | null, current: number | null): DeltaLabel | null {
  if (candidate == null) return null;
  if (current == null) return { kind: 'absolute', text: formatMoney(candidate), muted: false };
  const d = candidate - current;
  if (Math.abs(d) < 0.005) return { kind: 'same', text: 'Same price', muted: true };
  if (d > 0) return { kind: 'up', text: `+${formatMoney(d)}`, muted: false };
  // Muted absolute, NOT "−$X": informative without merchandising the saving.
  return { kind: 'down', text: formatMoney(candidate), muted: true };
}

/**
 * The largest catalog size this photo genuinely supports — cross-family,
 * paper-agnostic. Custom sizes are never proposed; ties break to the longer
 * side.
 *
 * QUALITY BAR (owner 2026-08-15, corrected): gated on `goodDpi` (200), NOT the
 * minimum-acceptable floor (100 C-Type / 180 fine art). The floor only means
 * "we will print it"; at exactly 100 dpi a 1200×800 phone shot would have been
 * advertised as printing "beautifully" at 8×12, which is a lie and would have
 * poisoned the one thing that makes this chip work — that it is honest. The
 * chip is permission to go big, so it must only name sizes that genuinely
 * look good.
 */
export function largestSupportedSize(
  catalog: Map<string, ResolvedSize>,
  photo: { width: number; height: number },
  thresholds: PrintCheckThresholds,
): ResolvedSize | null {
  let best: ResolvedSize | null = null;
  let bestArea = -1;
  for (const entry of catalog.values()) {
    if (isCustomSizeId(entry.sizeId)) continue;
    const result = checkPrint(photo, entry.inches, null, 'crop', entry.family, thresholds);
    if (result.status === 'lowres') continue;
    if (result.dpi < thresholds.goodDpi) continue;
    const area = entry.inches[0] * entry.inches[1];
    const longer = Math.max(entry.inches[0], entry.inches[1]);
    const bestLonger = best ? Math.max(best.inches[0], best.inches[1]) : -1;
    if (area > bestArea || (area === bestArea && longer > bestLonger)) {
      best = entry;
      bestArea = area;
    }
  }
  return best;
}

/** Area in square inches — for "is the permission size actually bigger". */
/** Orientation-agnostic aspect (long/short) — 12×16 and 16×12 are the same shape. */
function aspect(w: number, h: number): number {
  return Math.max(w, h) / Math.max(1, Math.min(w, h));
}

/* Ratio gate for RE-AIMED upsells (owner 2026-08-16): a 3:4 photo was being
   told "go bigger: 16×16" — a shape change dressed up as an upgrade, unlikely
   to convert. 12% keeps 16×20 for a 12×16 pick (6.7% off) and rejects square
   (33% off). The 'largest' ceiling mode is exempt: "how big CAN I print this"
   is honestly answered by area, and crop-loss already costs it DPI. */
const UPSELL_RATIO_TOL = 0.12;

function ratioClose(entry: ResolvedSize, photo: { width: number; height: number }): boolean {
  const [w, h] = entry.inches;
  return (
    Math.abs(Math.log(aspect(w, h) / aspect(photo.width, photo.height))) <=
    Math.log(1 + UPSELL_RATIO_TOL)
  );
}

export function sizeArea(entry: ResolvedSize): number {
  return entry.inches[0] * entry.inches[1];
}

/**
 * The fine art size with the SAME dimensions as a C-Type size (ct-8x12 ->
 * fa-8x12), or null when fine art doesn't stock those dimensions (4x6 etc) —
 * the eligibility gate for the "Fine Art" paper-tier chip.
 */
export function fineArtEquivalent(
  catalog: Map<string, ResolvedSize>,
  ctSizeId: string,
): ResolvedSize | null {
  let size: string;
  try {
    const parsed = parseSizeId(ctSizeId);
    if (parsed.family !== 'ctype') return null;
    size = parsed.size;
  } catch {
    return null;
  }
  for (const entry of catalog.values()) {
    if (entry.family !== 'fine_art') continue;
    if (isCustomSizeId(entry.sizeId)) continue;
    if (entry.size === size) return entry;
  }
  return null;
}

/**
 * Cheapest fine art upgrade delta from the current C-Type selection, or null
 * when there's no equivalent size / no priced paper. Drives the chip label
 * "Fine Art · from +$4.90".
 */
export function cheapestFineArtDelta(
  data: PricingData,
  catalog: Map<string, ResolvedSize>,
  ctSizeId: string,
  ctPaper: PaperId,
): { faSizeId: string; delta: DeltaLabel } | null {
  const fa = fineArtEquivalent(catalog, ctSizeId);
  if (!fa) return null;
  const current = priceForSafe(data, ctSizeId, paperForSize(data, ctSizeId, ctPaper));
  let cheapest: number | null = null;
  for (const paper of data.productLines.fine_art.papers) {
    const p = priceForSafe(data, fa.sizeId, paper);
    if (p != null && (cheapest == null || p < cheapest)) cheapest = p;
  }
  if (cheapest == null) return null;
  const delta = priceDeltaLabel(cheapest, current);
  return delta ? { faSizeId: fa.sizeId, delta } : null;
}





export type UpsellKind = 'largest' | 'fineart' | 'bigger';

export interface UpsellTarget {
  entry: ResolvedSize;
  /** Delta from the current pick (null when nothing is selected yet). */
  price: DeltaLabel | null;
  kind: UpsellKind;
  /** Trigger copy, e.g. `Prints up to 20×30"` / `Fine art 8×12"`. */
  label: string;
  /** Badge on the highlighted row in the dropdown. */
  badge: string;
}

/**
 * ONE adaptive button beside the size picker (owner 2026-08-15 round 4).
 *
 * It answers the only question asked at this step — "how big can I print
 * this?" — and then keeps earning its place as the customer changes their
 * mind:
 *   nothing / small pick   -> the LARGEST size the photo prints sharply at;
 *   a C-Type size chosen   -> the FINE ART upgrade (same dimensions if we
 *                             stock them, else the next fine-art size up),
 *                             because that is where the margin is;
 *   a fine-art size chosen -> the next fine-art size UP.
 * It never auto-applies: clicking opens the size list focused on that row.
 */
/**
 * The largest size this photo prints sharply at — pano-excluded, goodDpi-gated.
 * Drives the PERSISTENT "Largest for this photo" row marker in the size list
 * (owner 2026-08-15: accepting an upsell made the badge vanish, because it was
 * tied to the transient upsell target; the fact itself never changes).
 */
export function largestPrintable(
  catalog: Map<string, ResolvedSize>,
  photo: { width: number; height: number },
  thresholds: PrintCheckThresholds,
): ResolvedSize | null {
  let best: ResolvedSize | null = null;
  for (const e of catalog.values()) {
    if (isCustomSizeId(e.sizeId) || e.pano) continue;
    const res = checkPrint(photo, e.inches, null, 'crop', e.family, thresholds);
    if (res.status === 'lowres' || res.dpi < thresholds.goodDpi) continue;
    if (!best || sizeArea(e) > sizeArea(best)) best = e;
  }
  return best;
}

export function upsellTarget(
  data: PricingData,
  catalog: Map<string, ResolvedSize>,
  photo: { width: number; height: number },
  thresholds: PrintCheckThresholds,
  currentSizeId: string | null,
  currentPaper: PaperId,
  /** True once the customer has opened the suggestion — after that the button
   *  stops re-selling the ceiling and starts selling fine art. */
  seenLargest = false,
): UpsellTarget | null {
  const ok: ResolvedSize[] = [];
  for (const e of catalog.values()) {
    if (isCustomSizeId(e.sizeId) || e.pano) continue;
    const res = checkPrint(photo, e.inches, null, 'crop', e.family, thresholds);
    if (res.status === 'lowres' || res.dpi < thresholds.goodDpi) continue;
    ok.push(e);
  }
  if (!ok.length) return null;
  ok.sort((a, b) => sizeArea(b) - sizeArea(a));

  const current = currentSizeId ? catalog.get(currentSizeId) ?? null : null;
  const currentPrice =
    currentSizeId != null
      ? priceForSafe(data, currentSizeId, paperForSize(data, currentSizeId, currentPaper))
      : null;
  const priced = (e: ResolvedSize) => {
    const raw = priceDeltaLabel(
      priceForSafe(data, e.sizeId, paperForSize(data, e.sizeId, currentPaper)),
      currentPrice,
    );
    // The BUTTON never offers a downgrade (unlike list rows, which now show
    // the muted absolute) — an upsell priced below current is a non-offer.
    return raw && raw.kind === 'down' ? null : raw;
  };

  /* The 'largest' ceiling offer ("Prints up to 20×30", answering "how big CAN
     I print this?") was KILLED by the owner on 2026-09-18. It was the most
     shown surface on the portal and the least taken: 1,380 shown for 9
     accepts (0.7%) and $107 attributed between Aug 21 and Sep 17, against
     size_bigger at 16% and paper_premium at 10%. The `seenLargest` argument is
     kept so callers need not change; it no longer gates anything. With no size
     chosen there is now no button — the re-aimed offers below need a current
     size to aim from. */
  void seenLargest;

  if (!current) return null;

  // On C-Type -> push fine art (same dimensions if stocked, else next up).
  if (current.family === 'ctype') {
    const same = ok.find((e) => e.family === 'fine_art' && e.size === current.size);
    const up = ok
      .filter(
        (e) => e.family === 'fine_art' && sizeArea(e) >= sizeArea(current) && ratioClose(e, photo),
      )
      .sort((a, b) => sizeArea(a) - sizeArea(b))[0];
    const fa = same ?? up;
    if (fa) {
      return {
        entry: fa,
        price: priced(fa),
        kind: 'fineart',
        label: `Fine art ${sizeWithUnit(fa.size)}`,
        badge: 'Gallery quality',
      };
    }
  }

  /* Next size up — but only a MEANINGFUL one. "A2 for +$1.00" over a 16×24,
     or a 5×5 over a 4×6, is a rounding error dressed up as an upgrade. */
  const bigger = ok
    .filter((e) => sizeArea(e) >= sizeArea(current) * 1.25 && ratioClose(e, photo))
    .sort((a, b) => sizeArea(a) - sizeArea(b))[0];
  if (!bigger) return null;
  return {
    entry: bigger,
    price: priced(bigger),
    kind: 'bigger',
    label: `Go bigger · ${sizeWithUnit(bigger.size)}`,
    badge: 'Next size up',
  };
}

/* ---------- #9: wizard enlargement finale (owner 2026-08-15) ---------- */

export interface WizardEnlargement<T> {
  photo: T;
  entry: ResolvedSize;
}

/**
 * The wizard's "one to frame?" finale: after a bulk apply (typically 40× 4×6),
 * offer ONE enlargement of the batch's sharpest shot at 8×12. Rules:
 *  - skipped when the wizard size is already 8×12-or-bigger (no downgrade ask);
 *  - the pick is the photo with the HIGHEST DPI at 8×12, and it must clear
 *    goodDpi — the offer is a compliment ("this shot deserves it"), so it can
 *    never point at a photo that would print soft;
 *  - price is shown ABSOLUTE ("+$X") because it's an extra print, not a swap.
 */
export function wizardEnlargement<T extends { width: number; height: number }>(
  catalog: Map<string, ResolvedSize>,
  photos: T[],
  thresholds: PrintCheckThresholds,
  appliedSizeId: string,
  targetSizeId = 'ct-8x12',
): WizardEnlargement<T> | null {
  const entry = catalog.get(targetSizeId);
  if (!entry) return null;
  const applied = catalog.get(appliedSizeId);
  if (applied && sizeArea(applied) >= sizeArea(entry)) return null;
  let best: T | null = null;
  let bestDpi = 0;
  for (const p of photos) {
    const res = checkPrint(p, entry.inches, null, 'crop', entry.family, thresholds);
    if (res.status === 'lowres' || res.dpi < thresholds.goodDpi) continue;
    if (res.dpi > bestDpi) {
      bestDpi = res.dpi;
      best = p;
    }
  }
  return best ? { photo: best, entry } : null;
}

/* ---------- #2 round 2: premium paper upsell (owner 2026-08-16) ---------- */

/**
 * Entry-tier fine art -> premium equivalent, matched by CHARACTER (owner's
 * mapping): matt to the gallery cotton matt, lustre to the darkroom pearl.
 * Bauhaus Metallic is a specialty look, not an entry tier — no upsell.
 */
const PREMIUM_PAPER: Partial<Record<PaperId, PaperId>> = {
  'bauhaus-matt': 'ilford-cottonrag-smooth',
  'bauhaus-lustre': 'ilford-pearl',
};

export interface PaperUpsell {
  paper: PaperId;
  label: string;
  price: DeltaLabel;
}

/**
 * The paper twin of the size upsell button: on an entry-tier fine art paper,
 * offer its premium equivalent as a delta ("Cotton Rag Smooth · +$11.00").
 * Null when the paper has no premium twin, the twin isn't priced at this size
 * (specialty papers skip sizes), or the "upgrade" wouldn't cost more — a
 * priced offer is a made decision, so it must always be a real step up.
 */
export function premiumPaperUpsell(
  data: PricingData,
  sizeId: string,
  currentPaper: PaperId,
): PaperUpsell | null {
  const target = PREMIUM_PAPER[currentPaper];
  if (!target) return null;
  const price = priceForSafe(data, sizeId, target);
  const current = priceForSafe(data, sizeId, currentPaper);
  if (price == null || current == null) return null;
  const d = priceDeltaLabel(price, current);
  if (!d || d.kind !== 'up') return null;
  return { paper: target, label: shortPaperLabel(data, target), price: d };
}

/* ─────────── Anchor default + cart small-prints (owner 2026-08-17) ─────────── */

/** True when the size's ratio matches the photo within the same 2% tolerance
 *  as the "Fits your photo" badge — prints uncropped, no review friction. */
export function fitsRatio(
  entry: ResolvedSize,
  photo: { width: number; height: number },
): boolean {
  const photoRatio = Math.max(photo.width, photo.height) / Math.max(1, Math.min(photo.width, photo.height));
  const sizeRatio =
    Math.max(entry.inches[0], entry.inches[1]) / Math.min(entry.inches[0], entry.inches[1]);
  return Math.abs(sizeRatio - photoRatio) / photoRatio <= 0.02;
}

/* McDonald's anchor (owner 2026-08-17, "option A"): the blank-slate default
   size is the ratio-fitting size nearest the ~$6-8 band, NOT the cheapest
   print in the shop. From an 8×10/8×12 anchor the 4×6 row reads as a muted
   bargain below and fine art reads "+$7.55" above. QUALITY GATE: the anchor
   must clear goodDpi — never default anyone into a soft print. */
const ANCHOR_LADDER = ['ct-8x12', 'ct-8x10', 'ct-6x9', 'ct-6x8', 'ct-5x7', 'ct-4x6'];
/** The anchor sizes that count as a RAISED anchor for tracking purposes. */
export const ANCHOR_BAND = new Set(['ct-8x12', 'ct-8x10']);

export function anchorDefaultSize(
  catalog: Map<string, ResolvedSize>,
  photo: { width: number; height: number },
  thresholds: PrintCheckThresholds,
): string | null {
  for (const id of ANCHOR_LADDER) {
    const e = catalog.get(id);
    if (!e || !fitsRatio(e, photo)) continue;
    const res = checkPrint(photo, e.inches, null, 'crop', e.family, thresholds);
    if (res.status === 'lowres' || res.dpi < thresholds.goodDpi) continue;
    return id;
  }
  return null; // caller keeps the plain ct-4x6 bootstrap default
}

/* Cart-exit small-prints sheet (owner 2026-08-17). Budget rule, from the
   owner's own examples ($15 cart -> ~$4 offer, $200 cart -> ~$12) and the
   standard impulse-add-on evidence (fries ≈ 20-30% of a burger; add-ons
   convert best at/under ~25% of order value, and must feel like rounding
   error, not a line item): total = clamp(25% of cart, floor $1.40, cap $12).
   Tune the constants after the first acceptance readout — they are the knob. */
export const SMALLPRINTS_CART_FRACTION = 0.25;
export const SMALLPRINTS_CAP = 12;
export const SMALLPRINTS_FLOOR = 1.4;
// Squares included so 1:1 photos get offers too (5×5 $0.95 / 6×6 $1.35).
const SMALL_PRINT_SIZES = ['ct-4x6', 'ct-5x7', 'ct-6x8', 'ct-6x9', 'ct-5x5', 'ct-6x6'];

export interface SmallPrintsItem {
  photoId: string;
  sizeId: string;
  price: number;
}

export interface SmallPrintsOffer {
  items: SmallPrintsItem[];
  total: number;
  /** 'unprinted' = their photos with no prints yet; 'duplicates' = small
   *  copies of what they already printed (the lone-12×18 cart). */
  kind: 'unprinted' | 'duplicates';
}

export function smallPrintsOffer(
  data: PricingData,
  catalog: Map<string, ResolvedSize>,
  photos: Array<{ id: string; width: number; height: number; status: string }>,
  prints: Array<{ photoId: string; sizeId: string; qty: number; unitPrice?: number | null; frameId?: string | null }>,
  thresholds: PrintCheckThresholds,
  cartTotal: number,
  /** 'unprinted' = the no-prints-yet dialog's offer (photos being left out);
   *  'duplicates' = the cart-exit sheet (small copies of printed photos).
   *  Split (owner 2026-08-17) so the two moments never ask about the same
   *  photos twice. */
  mode: 'unprinted' | 'duplicates',
): SmallPrintsOffer | null {
  if (cartTotal <= 0 || prints.length === 0) return null;
  if (mode === 'duplicates') {
    // The cart sheet is an INTERRUPTION, so it must be earned: a big-ticket
    // print in the cart, or a small order overall. (The unprinted offer rides
    // an interruption that already exists — the no-prints warning — so it
    // fires whenever candidates fit the budget.)
    const totalQty = prints.reduce((a, p) => a + (p.qty || 0), 0);
    const hasBig = prints.some((p) => (p.unitPrice ?? 0) >= 15 || p.frameId);
    if (!hasBig && totalQty > 6) return null;
  }

  const budget = Math.max(SMALLPRINTS_FLOOR, Math.min(cartTotal * SMALLPRINTS_CART_FRACTION, SMALLPRINTS_CAP));
  const printedPhotoIds = new Set(prints.map((p) => p.photoId));
  const existing = new Set(prints.map((p) => `${p.photoId}|${p.sizeId}`));

  /** Cheapest PERFECT-ratio-fit small size for this photo, quality-gated —
   *  an upsell we initiate must never create a crop or low-res flag. */
  const candidate = (ph: { id: string; width: number; height: number }): SmallPrintsItem | null => {
    let best: SmallPrintsItem | null = null;
    for (const id of SMALL_PRINT_SIZES) {
      const e = catalog.get(id);
      if (!e || !fitsRatio(e, ph) || existing.has(`${ph.id}|${id}`)) continue;
      const res = checkPrint(ph, e.inches, null, 'crop', e.family, thresholds);
      if (res.status === 'lowres' || res.dpi < thresholds.goodDpi) continue;
      const price = priceForSafe(data, id, paperForSize(data, id, 'gloss' as PaperId));
      if (price == null) continue;
      if (!best || price < best.price) best = { photoId: ph.id, sizeId: id, price };
    }
    return best;
  };

  const ready = photos.filter((p) => p.status === 'ready');
  const pool =
    mode === 'unprinted'
      ? ready.filter((p) => !printedPhotoIds.has(p.id))
      : ready.filter((p) => printedPhotoIds.has(p.id));
  const items: SmallPrintsItem[] = [];
  let total = 0;
  for (const ph of pool) {
    const c = candidate(ph);
    if (!c) continue;
    if (total + c.price > budget) {
      if (items.length > 0) break;
      continue; // first candidate over budget — try a cheaper photo
    }
    items.push(c);
    total += c.price;
  }
  if (!items.length) return null;
  return { items, total: Math.round(total * 100) / 100, kind: mode };
}

